/**
 * WiseEntry — entry point.
 *
 * Wires the stage router, top-bar buttons, settings hydration, and
 * service-worker registration.
 */

import './styles.css';
import '../public/assets/readability.css';
import { getState, setState, subscribe } from './state.js';
import { loadAndApplySettings } from './settings.js';
import { tryOpenStorage, resetAllLocalData } from './lib/storage.js';
import { initTheme } from './lib/theme.js';
import { migrateAllRulesToCanonical } from './lib/rules.js';
import { listRulesBackupFilenames, loadStoredAppConfigHandle } from './folders.js';
import { renderFolderStage }  from './ui/stage-folder.js';
import { renderExtractStage } from './ui/stage-extract.js';
import { renderReviewStage }  from './ui/stage-review.js';
import { renderCardStage, teardownCardStage } from './ui/stage-card.js';
import { renderExportStage }  from './ui/stage-export.js';
import { openSettings }       from './ui/settings-screen.js';
import { openRulesEditor }    from './ui/rules-editor.js';
import { toast } from './ui/dom.js';
import { APP_VERSION } from './version.js';

const stageMount = document.getElementById('stage-mount');
const stagesNav  = document.getElementById('stages');
const batchMeta  = document.getElementById('batch-meta');

let lastStage = null;

const STAGE_ORDER = ['folder', 'extract', 'review', 'export'];

function render() {
  const state = getState();

  // Stage pills: active / done / disabled
  for (const pill of stagesNav.querySelectorAll('.stage-pill')) {
    pill.classList.remove('active', 'done');
    const target = pill.dataset.stage;
    const cur = state.stage === 'card' ? 'review' : state.stage;
    const ti = STAGE_ORDER.indexOf(target);
    const ci = STAGE_ORDER.indexOf(cur);
    if (target === cur) pill.classList.add('active');
    else if (ti < ci) pill.classList.add('done');

    if (target === 'extract' &&
        (!state.workHandle || state.files.length === 0 || !state.hasKey())) {
      pill.disabled = true;
    } else if (target === 'review' &&
        !state.files.some(f => f.status === 'done' || f.status === 'failed')) {
      pill.disabled = true;
    } else if (target === 'export' &&
        !state.files.some(f => f.status === 'done')) {
      pill.disabled = true;
    } else {
      pill.disabled = false;
    }
  }

  // Top-bar batch meta
  if (state.files.length > 0) {
    const done = state.files.filter(f => f.status === 'done').length;
    batchMeta.textContent = `${done} / ${state.files.length} extracted`;
  } else {
    batchMeta.textContent = '';
  }

  if (lastStage === 'card' && state.stage !== 'card') teardownCardStage();
  lastStage = state.stage;

  switch (state.stage) {
    case 'folder':  renderFolderStage(stageMount); break;
    case 'extract': renderExtractStage(stageMount); break;
    case 'review':  renderReviewStage(stageMount); break;
    case 'card':    renderCardStage(stageMount); break;
    case 'export':  renderExportStage(stageMount); break;
  }
}

stagesNav.addEventListener('click', (ev) => {
  const pill = ev.target.closest('.stage-pill');
  if (!pill || pill.disabled) return;
  setState({ stage: pill.dataset.stage });
});

document.getElementById('settings-btn').addEventListener('click', () => {
  openSettings();
});

document.getElementById('rules-btn').addEventListener('click', () => {
  openRulesEditor();
});

subscribe(render);

/* ---------- Boot ---------- */

(async () => {
  // 1. Browser support check
  if (!('showDirectoryPicker' in window)) {
    stageMount.innerHTML = `
      <div class="panel panel-empty">
        <h2>Browser <em>not supported</em></h2>
        <p>WiseEntry needs the File System Access API to read invoices from a folder and write the CSV back.</p>
        <p>Use a recent <strong>Chrome</strong>, <strong>Edge</strong>, or other Chromium-based browser. Safari and Firefox don't yet support this API.</p>
      </div>
    `;
    return;
  }

  // 2. IndexedDB must open (handles version drift and other open failures)
  const storage = await tryOpenStorage();
  if (!storage.ok) {
    const err = storage.error;
    const isVersion = err && err.name === 'VersionError';
    const errMsg = err && err.message ? String(err.message).replace(/</g, '&lt;') : 'unknown error';
    stageMount.innerHTML = `
      <div class="panel panel-empty">
        <h2>Local storage <em>couldn’t open</em></h2>
        ${isVersion ? `
        <p>Your browser has an older or newer WiseEntry database than this build expects. That usually happens after switching app versions.</p>
        ` : `
        <p>WiseEntry could not open its browser database (${errMsg}).</p>
        `}
        <p>You can clear WiseEntry’s data on <strong>this device</strong> and start fresh. Your invoice PDFs on disk are not affected.</p>
        <p><button type="button" class="primary-btn" id="storage-recovery-btn">Clear local data and reload</button></p>
        <hr style="margin:20px 0;border:none;border-top:1px solid var(--border)" />
        <p>If you use an <strong>app-config</strong> folder, rule backups may already exist under <code>backups/</code>. You can list them before clearing — copy a file out if you need it for import after recovery.</p>
        <p><button type="button" class="ghost-btn" id="storage-recovery-browse">Browse folder for backup files…</button></p>
        <pre id="storage-recovery-list" style="text-align:left;font-size:12px;max-height:180px;overflow:auto;background:var(--bg-secondary);padding:10px;border-radius:4px;display:none"></pre>
      </div>
    `;
    const btn = document.getElementById('storage-recovery-btn');
    if (btn) {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        try {
          await resetAllLocalData();
          location.reload();
        } catch (e) {
          console.error(e);
          btn.disabled = false;
          toast('Reset failed. Close other tabs with WiseEntry and try again.', 'info', 4000);
        }
      });
    }
    const browse = document.getElementById('storage-recovery-browse');
    const listEl = document.getElementById('storage-recovery-list');
    if (browse && listEl && 'showDirectoryPicker' in window) {
      browse.addEventListener('click', async () => {
        try {
          const dir = await window.showDirectoryPicker({ mode: 'read' });
          const names = await listRulesBackupFilenames(dir);
          listEl.style.display = 'block';
          listEl.textContent = names.length
            ? names.join('\n')
            : '(No rules-*.json or pre-migration-*.json in backups/ — pick the WiseEntry app-config folder.)';
        } catch (e) {
          if (e && e.name === 'AbortError') return;
          listEl.style.display = 'block';
          listEl.textContent = `Could not read folder: ${e && e.message ? e.message : e}`;
        }
      });
    } else if (browse) {
      browse.disabled = true;
    }
    return;
  }

  // 3. Load settings
  await loadAndApplySettings();
  initTheme(getState().settings);

  try {
    await migrateAllRulesToCanonical();
  } catch (e) {
    console.warn('Rule schema migration:', e);
  }

  // 4. Hydrate app-config handle from IndexedDB
  try {
    const handle = await loadStoredAppConfigHandle();
    if (handle) setState({ appConfigHandle: handle });
  } catch (err) {
    console.warn('Could not hydrate app-config handle:', err);
  }

  render();
  maybeShowRuleSchemaBannerV03();

  // 5. Service worker
  if ('serviceWorker' in navigator && location.protocol === 'https:') {
    navigator.serviceWorker.register('./sw.js').catch(err => {
      console.warn('Service worker registration failed:', err);
    });
  }

  // 6. PWA install prompt
  let deferredPrompt = null;
  window.addEventListener('beforeinstallprompt', (ev) => {
    ev.preventDefault();
    deferredPrompt = ev;
    showInstallChip(deferredPrompt);
  });
})();

function showInstallChip(promptEv) {
  const tools = document.querySelector('.titlebar-tools');
  if (!tools) return;
  const btn = document.createElement('button');
  btn.className = 'ghost-btn';
  btn.textContent = 'Install app';
  btn.title = 'Install WiseEntry as a desktop app';
  btn.addEventListener('click', async () => {
    promptEv.prompt();
    try {
      const choice = await promptEv.userChoice;
      if (choice.outcome === 'accepted') {
        toast('Installing WiseEntry…', 'success');
        btn.remove();
      }
    } catch { /* ignore */ }
  });
  tools.insertBefore(btn, tools.firstChild);
}

// Footer version
const ver = document.getElementById('footer-version');
if (ver) ver.textContent = `v${APP_VERSION}`;

function maybeShowRuleSchemaBannerV03() {
  if (localStorage.getItem('wiseentry.ruleSchemaBannerV03')) return;
  const app = document.getElementById('app');
  if (!app) return;
  const bar = document.createElement('div');
  bar.className = 'rule-migration-banner';
  bar.setAttribute('role', 'status');
  const inner = document.createElement('div');
  inner.className = 'rule-migration-banner-inner';
  const p = document.createElement('p');
  p.appendChild(document.createTextNode(
    'Vendor rules use the v0.3 schema (summary vs detail export, tax GL, line rules). Open '
  ));
  const strong = document.createElement('strong');
  strong.textContent = 'Vendor rules';
  p.appendChild(strong);
  p.appendChild(document.createTextNode(' from the title bar to review or import backups.'));
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'ghost-btn';
  btn.textContent = 'Dismiss';
  btn.addEventListener('click', () => {
    localStorage.setItem('wiseentry.ruleSchemaBannerV03', '1');
    bar.remove();
  });
  inner.appendChild(p);
  inner.appendChild(btn);
  bar.appendChild(inner);
  app.insertBefore(bar, app.firstChild);
}
